import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { NEWS } from "@/data/news";

/**
 * /news — Newsroom archive page. Extended set of formation milestones
 * and platform updates, grouped by quarter.
 */



const QUARTERS = [...new Set(NEWS.map((e) => e.date))];

const NewsPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.newsPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Newsroom"
          label="Formation Milestones · Platform Updates"
          title={
            <>
              The partnership&apos;s{" "}
              <span className="italic text-[var(--kr-gold)]">documented</span>{" "}
              record.
            </>
          }
          description="A small, considered archive of formation milestones, platform updates and counterparty engagement notes. Materials are written for sophisticated readers and are not investment advice."
          breadcrumb="Newsroom"
        />

        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-16">
            {QUARTERS.map((q) => {
              const items = NEWS.filter((e) => e.date === q);
              return (
                <div key={q}>
                  <div className="flex items-center gap-4 mb-8 pb-4 border-b border-[var(--kr-line)]">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {q}
                    </span>
                    <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                    <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                      {String(items.length).padStart(2, "0")} item
                      {items.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="space-y-0">
                    {items.map((e, idx) => (
                      <article
                        key={e.slug}
                        data-testid={`${LANDING.newsPageItemPrefix}${q
                          .toLowerCase()
                          .replace(/\s+/g, "-")}-${idx}`}
                        className="grid lg:grid-cols-12 gap-6 lg:gap-12 py-9 border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.02)] transition-colors duration-500 -mx-2 px-2"
                      >
                        <div className="lg:col-span-2 font-mono-label text-[10px] text-[var(--kr-gold)]">
                          {e.kind}
                        </div>
                        <div className="lg:col-span-7">
                          <h3 className="font-serif-display text-[var(--kr-text)] text-[1.35rem] lg:text-[1.5rem] leading-[1.3] mb-4 group-hover:text-[var(--kr-gold)] transition-colors duration-500">
                            <Link to={`/news/${e.slug}`}>{e.title}</Link>
                          </h3>
                          <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.85]">
                            {e.body}
                          </p>
                          <Link
                            to={`/news/${e.slug}`}
                            data-testid={`${LANDING.newsPageItemLinkPrefix}${e.slug}`}
                            className="inline-block mt-5 kr-link font-mono-label text-[10px]"
                          >
                            Read &amp; Share →
                          </Link>
                        </div>
                        <div className="lg:col-span-3 lg:text-right font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                          Documented · {e.date}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.2] max-w-[36ch]">
                Media and press enquiries are received on a{" "}
                <span className="italic text-[var(--kr-gold)]">documented</span>{" "}
                basis.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <Link
                to="/contact"
                data-testid={LANDING.newsPageMedia}
                className="kr-btn-primary"
              >
                Media Enquiries
                <span aria-hidden>→</span>
              </Link>
              <Link to="/perspectives" className="kr-btn-ghost">
                Perspectives
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default NewsPage;
